import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  FileUp,
  Loader2,
  Check,
  X,
  Trash2,
  Sparkles,
  Pencil,
  Wand2,
  Save as SaveIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SECTIONS } from "@/data/questions";
import { API, generateProjectQuestions } from "@/lib/api";
import {
  addCustomQuestions,
  loadCustomQuestions,
  deleteCustomQuestion,
  deleteImportBatch,
  updateCustomQuestion,
} from "@/lib/customQuestions";
import { loadProjectContext, saveProjectContext } from "@/lib/projectContext";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp";
const MAX_MB = 12;

export default function Import() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null); // { questions: [{text, sectionId, groupId}], source }
  const [customQs, setCustomQs] = useState(() => loadCustomQuestions());
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  // Project context (used both for personalized AI answers & question generation)
  const [projectCtx, setProjectCtx] = useState(() => loadProjectContext());
  const [projectSaved, setProjectSaved] = useState(false);
  const [genCount, setGenCount] = useState(15);
  const [isGenerating, setIsGenerating] = useState(false);

  const persistProjectCtx = () => {
    saveProjectContext(projectCtx);
    setProjectSaved(true);
    toast.success("Contexte projet enregistré");
    setTimeout(() => setProjectSaved(false), 2000);
  };

  const generateFromProject = async () => {
    const desc = (projectCtx || "").trim();
    if (desc.length < 20) {
      toast.error("Décris ton projet en au moins 20 caractères.");
      return;
    }
    setIsGenerating(true);
    setError(null);
    setPreview(null);
    try {
      // Auto-save context before generating
      saveProjectContext(projectCtx);
      const data = await generateProjectQuestions({
        description: desc,
        count: Number(genCount) || 15,
      });
      const withIds = {
        ...data,
        questions: (data.questions || []).map((q, i) => ({
          ...q,
          _pid: `p_${Date.now()}_${i}`,
        })),
      };
      setPreview(withIds);
      toast.success(`${data.count} question(s) générée(s)`);
    } catch (e) {
      setError(e.message || "Erreur de génération");
      toast.error("Échec de la génération");
    } finally {
      setIsGenerating(false);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    customQs.forEach((q) => {
      const key = q.importId || "orphan";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(q);
    });
    return Array.from(map.entries())
      .map(([importId, items]) => ({
        importId,
        items,
        ts: items[0]?.ts || 0,
        source: items[0]?.source || null,
      }))
      .sort((a, b) => b.ts - a.ts);
  }, [customQs]);

  const sectionMap = useMemo(
    () => new Map(SECTIONS.map((s) => [s.id, s])),
    []
  );

  const groupTitle = (sid, gid) => {
    const s = sectionMap.get(sid);
    if (!s) return "—";
    const g = s.groups.find((x) => x.id === gid) || s.groups[0];
    return `${s.short} · ${g.title}`;
  };

  const handleFile = (f) => {
    setError(null);
    setPreview(null);
    if (!f) return;
    const mb = f.size / (1024 * 1024);
    if (mb > MAX_MB) {
      setError(`Fichier trop volumineux (${mb.toFixed(1)} Mo, max ${MAX_MB} Mo).`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/import-questions`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const withIds = {
        ...data,
        questions: (data.questions || []).map((q, i) => ({
          ...q,
          _pid: `p_${Date.now()}_${i}`,
        })),
      };
      setPreview(withIds);
      toast.success(`${data.count} question(s) extraite(s)`);
    } catch (e) {
      setError(e.message || "Erreur inconnue");
      toast.error("Échec de l'import");
    } finally {
      setLoading(false);
    }
  };

  const updatePreviewQuestion = (idx, patch) => {
    setPreview((p) => {
      if (!p) return p;
      const next = [...p.questions];
      next[idx] = { ...next[idx], ...patch };
      return { ...p, questions: next };
    });
  };

  const removePreviewQuestion = (idx) => {
    setPreview((p) => {
      if (!p) return p;
      const next = p.questions.filter((_, i) => i !== idx);
      return { ...p, questions: next };
    });
  };

  const confirmImport = () => {
    if (!preview || preview.questions.length === 0) return;
    const { all } = addCustomQuestions(preview.questions, preview.source);
    setCustomQs(all);
    setPreview(null);
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
    toast.success(`${preview.questions.length} question(s) intégrée(s)`);
  };

  const onDeleteOne = (id) => {
    if (editId === id) cancelEdit();
    setCustomQs(deleteCustomQuestion(id));
  };
  const onDeleteBatch = (importId) => {
    cancelEdit();
    setCustomQs(deleteImportBatch(importId));
    toast.success("Import supprimé");
  };

  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editSection, setEditSection] = useState("");
  const [editGroup, setEditGroup] = useState("");

  const startEdit = (q) => {
    setEditId(q.id);
    setEditText(q.text);
    setEditSection(q.sectionId);
    setEditGroup(q.groupId);
  };
  const cancelEdit = () => {
    setEditId(null);
    setEditText("");
  };
  const saveEdit = () => {
    if (!editId) return;
    const trimmed = editText.trim();
    if (!trimmed) {
      toast.error("Le texte ne peut pas être vide");
      return;
    }
    const updated = updateCustomQuestion(editId, {
      text: trimmed,
      sectionId: editSection,
      groupId: editGroup,
    });
    setCustomQs(updated);
    cancelEdit();
    toast.success("Question mise à jour");
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 bg-grid">
      <header className="border-b border-zinc-800 px-5 md:px-8 py-4 flex items-center gap-4 flex-wrap">
        <Link
          to="/"
          data-testid="back-home"
          className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-sky-400 transition-colors uppercase tracking-widest"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </Link>
        <h1 className="font-display text-lg md:text-xl font-bold flex items-center gap-2">
          <FileUp className="w-5 h-5 text-sky-400" /> Import de questions
        </h1>
        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          PDF · JPG · PNG · WEBP · max {MAX_MB} Mo
        </span>
      </header>

      <main className="px-5 md:px-8 py-8 max-w-4xl mx-auto space-y-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-sky-400 font-bold">
          Extraction par IA · GPT-5.2 vision
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-black">
          Ajoute tes propres questions
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">
          Dépose un PDF, une photo de cours ou une capture d&apos;écran.
          L&apos;IA extrait les questions et les classe automatiquement dans
          les sections existantes. Tu valides, elles s&apos;ajoutent au corpus.
        </p>

        {/* Upload zone */}
        <div
          data-testid="import-dropzone"
          onDrop={onDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          className={`border-2 border-dashed p-8 md:p-12 text-center transition-colors ${
            dragOver
              ? "border-sky-400 bg-sky-500/5"
              : "border-zinc-800 bg-zinc-900/40"
          }`}
        >
          <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
          <div className="font-display text-lg text-zinc-200 mb-2">
            {file ? file.name : "Dépose un fichier ici"}
          </div>
          {file && (
            <div className="text-xs text-zinc-500 mb-3">
              {(file.size / (1024 * 1024)).toFixed(2)} Mo · {file.type || "type inconnu"}
            </div>
          )}
          {!file && (
            <div className="text-xs text-zinc-500 mb-4">
              ou clique pour parcourir
            </div>
          )}
          <input
            ref={inputRef}
            data-testid="file-input"
            type="file"
            accept={ACCEPT}
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <div className="flex justify-center gap-3 flex-wrap">
            <button
              data-testid="pick-file-btn"
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 px-4 py-2 text-xs uppercase tracking-widest text-zinc-200 transition-colors"
            >
              <FileUp className="w-3.5 h-3.5" />
              Choisir un fichier
            </button>
            <button
              data-testid="upload-btn"
              onClick={upload}
              disabled={!file || isLoading}
              className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-950 font-bold px-4 py-2 text-xs uppercase tracking-widest transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Extraction en cours…
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  Extraire les questions
                </>
              )}
            </button>
          </div>
          {error && (
            <div
              data-testid="import-error"
              className="mt-4 text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2 inline-block"
            >
              {error}
            </div>
          )}
        </div>

        {/* Mon projet — contexte personnel */}
        <section
          data-testid="project-context-section"
          className="bg-zinc-900 border border-zinc-800"
        >
          <div className="border-b border-zinc-800 px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Wand2 className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-bold">
                Mon projet · sur-mesure
              </div>
              <div className="font-display text-lg mt-0.5">
                Contexte personnel du candidat
              </div>
            </div>
            {projectCtx && (
              <span className="text-[10px] uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1">
                <Check className="w-3 h-3" /> Actif
              </span>
            )}
          </div>
          <div className="p-5 space-y-3">
            <p className="text-sm text-zinc-400 leading-relaxed">
              Décris ton projet en quelques phrases (outils, périmètre,
              rôle, contraintes). Ce contexte sera <strong className="text-zinc-200">
              injecté automatiquement dans chaque réponse IA</strong> et
              pourra générer des questions sur-mesure ci-dessous.
            </p>
            <textarea
              data-testid="project-context-textarea"
              value={projectCtx}
              onChange={(e) => setProjectCtx(e.target.value)}
              placeholder="Ex : Refonte AD Tier-0/1/2 chez un client industriel, bastion Wallix, supervision Centreon + ELK, mise en place PAM, PCA/PRA sur site distant, équipe de 4 admins…"
              rows={5}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none px-3 py-2 text-sm text-zinc-100 font-mono leading-relaxed resize-y"
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                {projectCtx.length} caractères · min 20
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="save-project-context"
                  onClick={persistProjectCtx}
                  className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-emerald-500/50 hover:text-emerald-400 px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors"
                >
                  <SaveIcon className="w-3.5 h-3.5" />
                  {projectSaved ? "Enregistré ✓" : "Enregistrer"}
                </button>
              </div>
            </div>

            <div className="mt-2 pt-4 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Nombre de questions
                </label>
                <input
                  data-testid="gen-count-input"
                  type="number"
                  min="5"
                  max="30"
                  value={genCount}
                  onChange={(e) => setGenCount(e.target.value)}
                  onBlur={(e) => {
                    const n = Number(e.target.value);
                    if (isNaN(n)) setGenCount(15);
                    else setGenCount(Math.max(5, Math.min(30, Math.round(n))));
                  }}
                  className="w-20 bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none px-2 h-9 text-sm text-zinc-100"
                />
                <span className="text-[10px] uppercase tracking-widest text-zinc-600">
                  (5–30)
                </span>
              </div>
              <button
                data-testid="generate-from-project"
                onClick={generateFromProject}
                disabled={isGenerating || (projectCtx || "").trim().length < 20}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-950 font-bold px-4 py-2 text-xs uppercase tracking-widest transition-colors"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Génération en cours…
                  </>
                ) : (
                  <>
                    <Wand2 className="w-3.5 h-3.5" />
                    Générer des questions sur mon projet
                  </>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Preview */}
        {preview && preview.questions.length > 0 && (
          <section
            data-testid="import-preview"
            className="bg-zinc-900 border border-sky-500/40 fade-up"
          >
            <div className="border-b border-zinc-800 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-sky-400 font-bold">
                  Prévisualisation
                </div>
                <div className="font-display text-lg mt-1">
                  {preview.questions.length} question
                  {preview.questions.length > 1 ? "s" : ""} extraite
                  {preview.questions.length > 1 ? "s" : ""}
                </div>
                {preview.source?.filename && (
                  <div className="text-xs text-zinc-500 mt-1">
                    depuis {preview.source.filename}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="cancel-import"
                  onClick={() => setPreview(null)}
                  className="border border-zinc-700 hover:border-zinc-500 px-4 py-2 text-xs uppercase tracking-widest text-zinc-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  data-testid="confirm-import"
                  onClick={confirmImport}
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold px-4 py-2 text-xs uppercase tracking-widest transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  Confirmer & intégrer
                </button>
              </div>
            </div>
            <div className="divide-y divide-zinc-800">
              {preview.questions.map((q, i) => (
                <div
                  key={q._pid}
                  data-testid={`preview-question-${i}`}
                  className="px-5 py-4 flex items-start gap-4"
                >
                  <div className="text-xs font-mono text-zinc-600 w-8 shrink-0 pt-2">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <textarea
                      data-testid={`preview-text-${i}`}
                      value={q.text}
                      onChange={(e) => updatePreviewQuestion(i, { text: e.target.value })}
                      rows={2}
                      className="w-full bg-zinc-950 border border-zinc-800 focus:border-sky-500 focus:outline-none px-3 py-2 text-sm text-zinc-100 font-mono resize-y"
                    />
                    <div className="flex gap-2 flex-wrap">
                      <Select
                        value={q.sectionId}
                        onValueChange={(v) => {
                          const s = sectionMap.get(v);
                          const g = s?.groups?.[0]?.id || q.groupId;
                          updatePreviewQuestion(i, { sectionId: v, groupId: g });
                        }}
                      >
                        <SelectTrigger
                          data-testid={`preview-section-${i}`}
                          className="bg-zinc-950 border-zinc-800 rounded-none h-9 w-[260px] text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SECTIONS.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={q.groupId}
                        onValueChange={(v) => updatePreviewQuestion(i, { groupId: v })}
                      >
                        <SelectTrigger
                          data-testid={`preview-group-${i}`}
                          className="bg-zinc-950 border-zinc-800 rounded-none h-9 w-[220px] text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(sectionMap.get(q.sectionId)?.groups || []).map((g) => (
                            <SelectItem key={g.id} value={g.id}>
                              {g.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <button
                    data-testid={`preview-remove-${i}`}
                    onClick={() => removePreviewQuestion(i)}
                    className="text-zinc-500 hover:text-red-400 transition-colors mt-2"
                    aria-label="Retirer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Existing imported */}
        {customQs.length > 0 && (
          <section data-testid="custom-list">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Questions importées ({customQs.length})
            </div>
            <div className="space-y-4">
              {grouped.map((batch) => (
                <div
                  key={batch.importId}
                  data-testid={`import-batch-${batch.importId}`}
                  className="bg-zinc-900 border border-zinc-800"
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-sky-400 font-bold">
                        Import
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {new Date(batch.ts).toLocaleString("fr-FR")}
                        {batch.source?.filename ? ` · ${batch.source.filename}` : ""}
                        {" · "}
                        {batch.items.length} question
                        {batch.items.length > 1 ? "s" : ""}
                      </div>
                    </div>
                    <button
                      data-testid={`delete-batch-${batch.importId}`}
                      onClick={() => onDeleteBatch(batch.importId)}
                      className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-red-500/40 hover:text-red-400 px-3 py-1.5 text-[11px] uppercase tracking-widest text-zinc-400 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Supprimer l&apos;import
                    </button>
                  </div>
                  <div className="divide-y divide-zinc-800">
                    {batch.items.map((q) => {
                      const isEditing = editId === q.id;
                      if (isEditing) {
                        const editSectionObj = sectionMap.get(editSection);
                        const editGroups = editSectionObj?.groups || [];
                        return (
                          <div
                            key={q.id}
                            data-testid={`edit-row-${q.id}`}
                            className="px-4 py-4 space-y-3 bg-sky-500/5"
                          >
                            <textarea
                              data-testid={`edit-text-${q.id}`}
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              rows={2}
                              className="w-full bg-zinc-950 border border-zinc-800 focus:border-sky-500 focus:outline-none px-3 py-2 text-sm text-zinc-100 font-mono resize-y"
                            />
                            <div className="flex gap-2 flex-wrap">
                              <Select
                                value={editSection}
                                onValueChange={(v) => {
                                  const s = sectionMap.get(v);
                                  setEditSection(v);
                                  setEditGroup(s?.groups?.[0]?.id || "");
                                }}
                              >
                                <SelectTrigger
                                  data-testid={`edit-section-${q.id}`}
                                  className="bg-zinc-950 border-zinc-800 rounded-none h-9 w-[260px] text-xs"
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {SECTIONS.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                      {s.title}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={editGroup}
                                onValueChange={setEditGroup}
                              >
                                <SelectTrigger
                                  data-testid={`edit-group-${q.id}`}
                                  className="bg-zinc-950 border-zinc-800 rounded-none h-9 w-[220px] text-xs"
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {editGroups.map((g) => (
                                    <SelectItem key={g.id} value={g.id}>
                                      {g.title}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="flex-1" />
                              <button
                                data-testid={`cancel-edit-${q.id}`}
                                onClick={cancelEdit}
                                className="border border-zinc-700 hover:border-zinc-500 px-3 py-1.5 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors"
                              >
                                Annuler
                              </button>
                              <button
                                data-testid={`save-edit-${q.id}`}
                                onClick={saveEdit}
                                className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors"
                              >
                                <Check className="w-3 h-3" />
                                Enregistrer
                              </button>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={q.id}
                          data-testid={`custom-question-${q.id}`}
                          className="px-4 py-3 flex items-start gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-sky-400 font-bold mb-1">
                              {groupTitle(q.sectionId, q.groupId)}
                            </div>
                            <div className="text-sm text-zinc-100">{q.text}</div>
                          </div>
                          <button
                            data-testid={`edit-custom-${q.id}`}
                            onClick={() => startEdit(q)}
                            className="text-zinc-500 hover:text-sky-400 transition-colors"
                            aria-label="Éditer"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`delete-custom-${q.id}`}
                            onClick={() => onDeleteOne(q.id)}
                            className="text-zinc-500 hover:text-red-400 transition-colors"
                            aria-label="Supprimer"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <button
                data-testid="go-home"
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold px-5 py-2.5 text-sm uppercase tracking-widest transition-colors"
              >
                Voir toutes les questions →
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
